import { useMemo, useState } from 'react';
import { Undo2, Check } from 'lucide-react';
import type { Facture, FactureLigne } from '../types';
import { formatEUR } from '../utils';

type LineSel = { include: boolean; quantite: number };

export function AvoirModal({
  facture,
  onCancel,
  onConfirm,
}: {
  facture: Facture;
  onCancel: () => void;
  onConfirm: (lignes: FactureLigne[], motif: string) => void;
}) {
  const [mode, setMode] = useState<'total' | 'partiel'>('total');
  const [motif, setMotif] = useState('');
  const [sel, setSel] = useState<LineSel[]>(
    facture.lignes.map((l) => ({ include: true, quantite: Math.abs(l.quantite) }))
  );

  const updateSel = (i: number, patch: Partial<LineSel>) =>
    setSel((s) => s.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));

  const lignesAvoir = useMemo<FactureLigne[]>(() => {
    if (mode === 'total') {
      return facture.lignes.map((l) => ({ ...l, quantite: -Math.abs(l.quantite) }));
    }
    return facture.lignes
      .map((l, i) => ({ src: l, s: sel[i] }))
      .filter(({ s }) => s.include && s.quantite > 0)
      .map(({ src, s }) => ({ ...src, quantite: -Math.abs(s.quantite) }));
  }, [mode, sel, facture.lignes]);

  const totalAvoir = useMemo(() => {
    const ht = lignesAvoir.reduce((acc, l) => acc + l.quantite * l.prixUnitaireHT, 0);
    const ttc = lignesAvoir.reduce(
      (acc, l) => acc + l.quantite * l.prixUnitaireHT * (1 + (l.tvaPct || 0) / 100),
      0
    );
    return { ht, ttc };
  }, [lignesAvoir]);

  const canConfirm = lignesAvoir.length > 0;

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-slate-900 border border-slate-800 rounded-xl w-full max-w-3xl my-8">
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
          <h2 className="text-lg font-heading text-slate-100 flex items-center gap-3">
            <Undo2 className="text-orange-400" size={20} strokeWidth={1.5} />
            Créer un avoir sur <span className="font-mono text-amber-300">{facture.numero}</span>
          </h2>
          <div className="flex gap-2">
            <button onClick={onCancel} className="px-3 py-1.5 text-sm text-slate-400 hover:text-slate-200">
              Annuler
            </button>
            <button
              onClick={() => onConfirm(lignesAvoir, motif)}
              disabled={!canConfirm}
              className="flex items-center gap-2 px-4 py-1.5 bg-orange-500 hover:bg-orange-400 text-slate-950 rounded-md text-sm font-medium disabled:opacity-40"
            >
              <Check size={14} strokeWidth={1.5} /> Créer l'avoir
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          <div className="inline-flex rounded-md border border-slate-800 bg-slate-950/40 p-1">
            <button
              onClick={() => setMode('total')}
              className={`px-3 py-1.5 text-xs rounded ${mode === 'total' ? 'bg-orange-500/20 text-orange-200' : 'text-slate-400 hover:text-slate-200'}`}
            >
              Avoir total
            </button>
            <button
              onClick={() => setMode('partiel')}
              className={`px-3 py-1.5 text-xs rounded ${mode === 'partiel' ? 'bg-orange-500/20 text-orange-200' : 'text-slate-400 hover:text-slate-200'}`}
            >
              Avoir partiel (lignes / quantités)
            </button>
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1">
              Motif de l'avoir (apparaît dans les notes)
            </label>
            <input
              value={motif}
              onChange={(ev) => setMotif(ev.target.value)}
              placeholder="Ex. annulation événement, erreur de facturation, geste commercial…"
              className="w-full px-3 py-2 bg-slate-950/60 border border-slate-800 rounded-md text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-amber-500/50"
            />
          </div>

          {mode === 'partiel' && (
            <div className="space-y-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">
                Lignes à créditer
              </div>
              {facture.lignes.map((l, i) => {
                const s = sel[i];
                return (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center bg-slate-950/40 rounded-md p-2 border border-slate-800">
                    <input
                      type="checkbox"
                      checked={s.include}
                      onChange={(ev) => updateSel(i, { include: ev.target.checked })}
                      className="col-span-1 accent-orange-500"
                    />
                    <div className="col-span-6 text-xs text-slate-300 truncate" title={l.description}>
                      {l.description || <span className="text-slate-500">(sans description)</span>}
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        Original : {Math.abs(l.quantite)} × {formatEUR(l.prixUnitaireHT)} HT
                      </div>
                    </div>
                    <div className="col-span-2">
                      <input
                        type="number"
                        step="0.01"
                        min={0}
                        max={Math.abs(l.quantite)}
                        value={s.quantite}
                        onChange={(ev) => updateSel(i, { quantite: parseFloat(ev.target.value) || 0 })}
                        disabled={!s.include}
                        className="w-full px-2 py-1 bg-slate-950/60 border border-slate-800 rounded text-xs text-slate-200 disabled:opacity-40"
                      />
                    </div>
                    <div className="col-span-3 text-right text-xs text-orange-300">
                      {formatEUR(-(s.include ? s.quantite : 0) * l.prixUnitaireHT)} HT
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="rounded-md border border-orange-500/30 bg-orange-500/5 p-4 flex justify-between text-sm">
            <div className="text-slate-300">
              Total avoir{' '}
              <span className="text-[11px] text-slate-500">
                ({lignesAvoir.length} ligne{lignesAvoir.length > 1 ? 's' : ''})
              </span>
            </div>
            <div className="text-right">
              <div className="text-orange-300 font-medium">{formatEUR(totalAvoir.ttc)} TTC</div>
              <div className="text-[11px] text-slate-500">{formatEUR(totalAvoir.ht)} HT</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AvoirModal;
